import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useThemeStore } from '../../state/theme';
import { useAuthStore } from '../../state/auth';
// import { useNotifState } from '../../state/notifs';
import '../../styles/Sidebar.css';
import {
  MdOutlineKeyboardDoubleArrowRight,
  MdOutlineKeyboardDoubleArrowLeft,
  MdExpandMore,
  MdExpandLess,
} from 'react-icons/md';
import { ROUTE_PERMISSIONS } from '../../constants';
import { FaPowerOff } from 'react-icons/fa';
import { HiOutlineBriefcase } from 'react-icons/hi';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { HiOutlineUserCircle } from 'react-icons/hi';
import LogoIcon from './LogoIcon';
import { getFirstLetter } from '../../utils';
import { LogOut } from 'lucide-react';

const Sidebar: React.FC = () => {
  const { isDarkMode, isCollapsed, toggleCollapsed } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, role, permissions } = useAuthStore();
  // const { showNotification } = useNotifState();
  const auth = useAuthStore();

  const hasPermission = (permission: string): boolean => {
    if (role === 'Admin') return true; // Admin has access to everything
    return permissions.includes(permission);
  };

  const hasAgentSubmenuAccess =
    hasPermission(ROUTE_PERMISSIONS.agentInvoice) ||
    hasPermission(ROUTE_PERMISSIONS.mismatchRequests) ||
    hasPermission(ROUTE_PERMISSIONS.exportLogs);
  const hasClientSubmenuAccess =
    hasPermission(ROUTE_PERMISSIONS.clientInvoice) ||
    hasPermission(ROUTE_PERMISSIONS.rcse);

  const [agentSubmenuOpen, setAgentSubmenuOpen] = useState(
    location.pathname.startsWith('/agents') ||
      (!hasPermission(ROUTE_PERMISSIONS.agents) && hasAgentSubmenuAccess)
  );
  const [brandSubmenuOpen, setBrandSubmenuOpen] = useState(
    location.pathname.startsWith('/clients') ||
      (!hasPermission(ROUTE_PERMISSIONS.clients) && hasClientSubmenuAccess)
  );

  const handleLogout = () => {
    logout();

    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 1000);
  };

  const isAgentRouteActive = location.pathname.startsWith('/agents');
  const isBrandRouteActive = location.pathname.startsWith('/clients');

  const toggleAgentSubmenu = (e: React.MouseEvent) => {
    if (isCollapsed) return;
    e.preventDefault();
    setAgentSubmenuOpen(!agentSubmenuOpen);
  };

  const toggleBrandSubmenu = (e: React.MouseEvent) => {
    if (isCollapsed) return;
    e.preventDefault();
    setBrandSubmenuOpen(!brandSubmenuOpen);
  };

  // Main menu should be visible if user has access to main menu OR any submenu
  const hasAgentAccess =
    hasPermission(ROUTE_PERMISSIONS.agents) || hasAgentSubmenuAccess;
  const hasClientAccess =
    hasPermission(ROUTE_PERMISSIONS.clients) || hasClientSubmenuAccess;

  return (
    <aside
      className={`font-inter sticky ${isCollapsed ? 'w-20' : 'w-64'} ${
        isDarkMode ? 'bg-gray-800' : 'bg-white border-r border-gray-200'
      } flex flex-col h-screen top-0 transition-all duration-300`}
    >
      <button
        onClick={toggleCollapsed}
        className={`absolute -right-4 top-4 p-1 rounded-md shadow-md ${
          isDarkMode
            ? 'text-gray-300 hover:bg-gray-700 bg-gray-800'
            : 'text-gray-600 hover:bg-gray-100 bg-white'
        }`}
        title={isCollapsed ? 'Expand' : 'Collapse'}
      >
        {isCollapsed ? (
          <MdOutlineKeyboardDoubleArrowRight />
        ) : (
          <MdOutlineKeyboardDoubleArrowLeft />
        )}
      </button>

      <div className="flex items-center">
        <div
          className={`px-4 cursor-pointer flex w-full flex-col`}
          onClick={() => navigate('/')}
        >
          <LogoIcon collapsed={isCollapsed} />
        </div>
      </div>

      <nav className="mt-8 flex-grow">
        <ul className="space-y-2 px-4">
          {hasPermission(ROUTE_PERMISSIONS.jobListings) && (
            <li>
              <NavLink
                to="/job-listings"
                className={({ isActive }) =>
                  `flex text-sm items-center ${isCollapsed ? 'justify-center' : 'px-4'} py-3 rounded-lg ${
                    isDarkMode
                      ? `text-gray-300 ${isActive ? 'bg-gray-700 font-semibold' : 'hover:bg-gray-700'}`
                      : `text-gray-600 ${isActive ? 'bg-gray-100 text-blackEighty font-semibold' : 'text-blackFourty hover:bg-gray-100'}`
                  } transition-colors`
                }
                title={isCollapsed ? 'Job Applications' : ''}
              >
                <span>
                  {isCollapsed ? (
                    <HiOutlineBriefcase className="w-5 h-5" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <HiOutlineBriefcase className="w-5 h-5" />
                      <span>Job Listings</span>
                    </div>
                  )}
                </span>
              </NavLink>
            </li>
          )}
          {hasPermission(ROUTE_PERMISSIONS.jobApplications) && (
            <li>
              <NavLink
                to="/jobs"
                className={({ isActive }) =>
                  `flex text-sm items-center ${isCollapsed ? 'justify-center' : 'px-4'} py-3 rounded-lg ${
                    isDarkMode
                      ? `text-gray-300 ${isActive ? 'bg-gray-700 font-semibold' : 'hover:bg-gray-700'}`
                      : `text-gray-600 ${isActive ? 'bg-gray-100 text-blackEighty font-semibold' : 'text-blackFourty hover:bg-gray-100'}`
                  } transition-colors`
                }
                title={isCollapsed ? 'Job Applications' : ''}
              >
                <span>
                  {isCollapsed ? (
                    <HiOutlineDocumentText className="w-5 h-5" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <HiOutlineDocumentText className="w-5 h-5" />
                      <span>Job Applications</span>
                    </div>
                  )}
                </span>
              </NavLink>
            </li>
          )}
          {hasAgentAccess && (
            <li className="relative">
              <div className="flex flex-col">
                <div
                  className={`flex text-sm items-center ${
                    isCollapsed ? 'justify-center py-3' : ''
                  } rounded-lg ${
                    isDarkMode
                      ? `text-gray-300 ${
                          isAgentRouteActive
                            ? 'bg-gray-700 font-semibold'
                            : 'hover:bg-gray-700'
                        }`
                      : `text-gray-600 ${
                          isAgentRouteActive
                            ? 'bg-gray-100 text-blackEighty font-semibold'
                            : 'text-blackFourty hover:bg-gray-100'
                        }`
                  } transition-colors ${!isCollapsed ? 'cursor-pointer' : ''}`}
                  // onClick={toggleAgentSubmenu}
                >
                  {isCollapsed ? (
                    <NavLink
                      to="/agents"
                      className={({ isActive }) =>
                        `flex justify-center items-center ${
                          isDarkMode ? `text-gray-300` : `text-gray-600`
                        }`
                      }
                    >
                      <HiOutlineUserCircle className="w-5 h-5" />
                    </NavLink>
                  ) : // If user has main agents permission, show as NavLink, otherwise just as label
                  hasPermission(ROUTE_PERMISSIONS.agents) ? (
                    <NavLink
                      to="/agents"
                      end
                      className={({ isActive }) =>
                        `flex items-center justify-between w-full rounded-lg py-3 px-4 ${
                          isDarkMode
                            ? `text-gray-300 ${
                                isActive
                                  ? 'bg-gray-700 font-semibold'
                                  : 'hover:bg-gray-700'
                              }`
                            : `text-gray-600 ${
                                isActive
                                  ? 'bg-gray-100 text-blackEighty font-semibold'
                                  : 'text-blackFourty hover:bg-gray-100'
                              }`
                        } transition-colors`
                      }
                    >
                      <div className="flex items-start gap-2">
                        <HiOutlineUserCircle className="w-5 h-5" />
                        <span>Agents</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleAgentSubmenu(e);
                        }}
                      >
                        {hasAgentSubmenuAccess ? (
                          agentSubmenuOpen ? (
                            <MdExpandLess className="w-5 h-5" />
                          ) : (
                            <MdExpandMore className="w-5 h-5" />
                          )
                        ) : null}
                      </button>
                    </NavLink>
                  ) : (
                    // User only has submenu access, show as non-clickable label
                    <div
                      className={`flex items-center justify-between w-full rounded-lg py-3 px-4 ${
                        isDarkMode
                          ? 'text-gray-300 cursor-default'
                          : 'text-gray-600 cursor-default'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <HiOutlineUserCircle className="w-5 h-5" />
                        <span>Agents</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleAgentSubmenu(e);
                        }}
                      >
                        {hasAgentSubmenuAccess ? (
                          agentSubmenuOpen ? (
                            <MdExpandLess className="w-5 h-5" />
                          ) : (
                            <MdExpandMore className="w-5 h-5" />
                          )
                        ) : null}
                      </button>
                    </div>
                  )}
                </div>

                {/*  only show when not collapsed and submenu is open */}
                {!isCollapsed && agentSubmenuOpen && (
                  <ul className="pl-9 mt-1 space-y-1">
                    {hasPermission(ROUTE_PERMISSIONS.agentInvoice) && (
                      <SubMenu
                        title="Invoices"
                        path="/agents/invoice"
                        isActive={location.pathname.startsWith(
                          '/agents/invoice'
                        )}
                        isDarkMode={isDarkMode}
                      />
                    )}
                    {hasPermission(ROUTE_PERMISSIONS.mismatchRequests) && (
                      <SubMenu
                        title="Mismatch Requests"
                        path="/agents/mismatch-requests"
                        isActive={location.pathname.startsWith(
                          '/agents/mismatch-requests'
                        )}
                        isDarkMode={isDarkMode}
                      />
                    )}
                    {hasPermission(ROUTE_PERMISSIONS.leaveRequests) && (
                      <SubMenu
                        title="Leave Requests"
                        path="/agents/leave-requests"
                        isActive={location.pathname.startsWith(
                          '/agents/leave-requests'
                        )}
                        isDarkMode={isDarkMode}
                      />
                    )}
                    {hasPermission(ROUTE_PERMISSIONS.flexiRequests) && (
                      <SubMenu
                        title="Flexi Requests"
                        path="/agents/flexi-requests"
                        isActive={location.pathname.startsWith(
                          '/agents/flexi-requests'
                        )}
                        isDarkMode={isDarkMode}
                      />
                    )}
                    {hasPermission(ROUTE_PERMISSIONS.exportLogs) && (
                      <SubMenu
                        title="Export Logs"
                        path="/export-logs"
                        isActive={location.pathname.startsWith('/export-logs')}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </ul>
                )}
              </div>
            </li>
          )}

          {hasClientAccess && (
            <li>
              <div className="flex flex-col">
                <div
                  className={`flex text-sm items-center ${
                    isCollapsed ? 'justify-center py-3' : ''
                  } rounded-lg ${
                    isDarkMode
                      ? `text-gray-300 ${
                          isBrandRouteActive
                            ? 'bg-gray-700 font-semibold'
                            : 'hover:bg-gray-700'
                        }`
                      : `text-gray-600 ${
                          isBrandRouteActive
                            ? 'bg-gray-100 text-blackEighty font-semibold'
                            : 'text-blackFourty hover:bg-gray-100'
                        }`
                  } transition-colors ${!isCollapsed ? 'cursor-pointer' : ''}`}
                  //  onClick={toggleBrandSubmenu}
                >
                  {isCollapsed ? (
                    <NavLink
                      to="/clients"
                      className={({ isActive }) =>
                        `flex justify-center items-center ${
                          isDarkMode ? `text-gray-300` : `text-gray-600`
                        }`
                      }
                    >
                      <HiOutlineOfficeBuilding className="w-5 h-5" />
                    </NavLink>
                  ) : // If user has main clients permission, show as NavLink, otherwise just as label
                  hasPermission(ROUTE_PERMISSIONS.clients) ? (
                    <NavLink
                      to="/clients"
                      end
                      className={({ isActive }) =>
                        `flex items-center justify-between w-full rounded-lg px-4 py-3 ${
                          isDarkMode
                            ? `text-gray-300 ${
                                isActive
                                  ? 'bg-gray-700 font-semibold'
                                  : 'hover:bg-gray-700'
                              }`
                            : `text-gray-600 ${
                                isActive
                                  ? 'bg-gray-100 text-blackEighty font-semibold'
                                  : 'text-blackFourty hover:bg-gray-100'
                              }`
                        } transition-colors`
                      }
                    >
                      <div className="flex items-start gap-2">
                        <HiOutlineOfficeBuilding className="w-5 h-5" />
                        <span>Clients</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBrandSubmenu(e);
                        }}
                      >
                        {hasClientSubmenuAccess ? (
                          brandSubmenuOpen ? (
                            <MdExpandLess className="w-5 h-5" />
                          ) : (
                            <MdExpandMore className="w-5 h-5" />
                          )
                        ) : null}
                      </button>
                    </NavLink>
                  ) : (
                    // User only has submenu access, show as non-clickable label
                    <div
                      className={`flex items-center justify-between w-full rounded-lg px-4 py-3 ${
                        isDarkMode
                          ? 'text-gray-300 cursor-default'
                          : 'text-gray-600 cursor-default'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <HiOutlineOfficeBuilding className="w-5 h-5" />
                        <span>Clients</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBrandSubmenu(e);
                        }}
                      >
                        {hasClientSubmenuAccess ? (
                          brandSubmenuOpen ? (
                            <MdExpandLess className="w-5 h-5" />
                          ) : (
                            <MdExpandMore className="w-5 h-5" />
                          )
                        ) : null}
                      </button>
                    </div>
                  )}
                </div>

                {/* only show when not collapsed and submenu is open */}
                {!isCollapsed && brandSubmenuOpen && (
                  <ul className="pl-9 mt-1 space-y-1">
                    {hasPermission(ROUTE_PERMISSIONS.clientInvoice) && (
                      <SubMenu
                        title="Invoices"
                        path="/clients/invoice"
                        isActive={location.pathname.startsWith(
                          '/clients/invoice'
                        )}
                        isDarkMode={isDarkMode}
                      />
                    )}
                    {hasPermission(ROUTE_PERMISSIONS.rcse) && (
                      <SubMenu
                        title="RCSE"
                        path="/clients/rcse"
                        isActive={location.pathname.startsWith('/clients/rcse')}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </ul>
                )}
              </div>
            </li>
          )}
        </ul>
      </nav>

      <div
        className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
      >
        {auth.name && !isCollapsed && (
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`flex items-center justify-center rounded-full text-sm font-medium uppercase ${
                isDarkMode
                  ? 'bg-gray-200 text-gray-800'
                  : 'bg-gray-200 text-gray-800'
              } w-10 h-10`}
              aria-hidden="true"
            >
              {getFirstLetter(auth.name)}
            </div>
            <p
              className={`text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-800'
              }`}
            >
              {auth.name}
            </p>
          </div>
        )}

        {/* {!isCollapsed && (
    <div className="flex text-sm items-center gap-2 px-4 py-3">
      <span
        onClick={toggleDarkMode}
        className={`cursor-pointer ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}
      >
        {isDarkMode ? 'Dark Mode' : 'Light Mode'}
      </span>
      <Switch
        checked={isDarkMode}
        onChange={toggleDarkMode}
        className="top-[0.5px]"
      />
    </div>
  )} */}

        <button
          onClick={handleLogout}
          className={`w-full flex text-sm items-center ${
            isCollapsed ? 'justify-center' : 'px-2'
          } py-2 rounded-md ${
            isDarkMode
              ? 'text-red-300 hover:bg-gray-700'
              : 'text-red-600 hover:bg-red-100'
          } transition-colors`}
          title={isCollapsed ? 'Logout' : ''}
        >
          {isCollapsed ? (
            <FaPowerOff className="size-4 fill-red-500" />
          ) : (
            <div className="flex items-center gap-2 pl-1">
              <LogOut className="size-4" />
              <span>Logout</span>
            </div>
          )}
        </button>
      </div>
    </aside>
  );
};

const SubMenu = ({
  title,
  path,
  isActive,
  isDarkMode,
}: {
  title: string;
  path: string;
  isActive: boolean;
  isDarkMode: boolean;
}) => {
  return (
    <li>
      <NavLink
        to={path}
        className={({ isActive }) =>
          `block text-xs py-2 px-2 rounded-md ${isDarkMode ? `text-gray-300 ${isActive ? 'bg-gray-700' : 'hover:bg-gray-700'}` : `${isActive ? 'bg-gray-100 text-blackEighty' : 'text-blackFourty hover:bg-gray-100'}`}`
        }
      >
        {title}
      </NavLink>
    </li>
  );
};

export default Sidebar;
